import { Mark, mergeAttributes } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'

export const CONTENT_LINK_PREFIX = '/browser/content?file='

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    contentLink: {
      setContentLink: (attributes: { target: string; page?: number; text?: string | null }) => ReturnType
      toggleContentLink: (attributes: { target: string; page?: number; text?: string | null }) => ReturnType
      unsetContentLink: () => ReturnType
    }
  }
}

const createContentLink = (openFile: (payload: { fileId: string; pageIndex?: number }) => void) =>
  Mark.create({
    name: 'contentLink',
    priority: 1000,

    addAttributes() {
      return {
        target: {
          default: null,
          parseHTML: (element) => {
            const href = element.getAttribute('href')
            if (!href) return null
            const url = new URL(href, window.location.origin)
            return url.searchParams.get('file')
          },
          renderHTML: (attributes) => {
            if (!attributes.target) {
              return {}
            }
            const pageParam = attributes.page ? `&page=${attributes.page}` : ''
            return {
              href: `${CONTENT_LINK_PREFIX}${attributes.target}${pageParam}`,
            }
          },
        },
        page: {
          default: null,
          parseHTML: (element) => {
            const href = element.getAttribute('href')
            if (!href) return null
            const url = new URL(href, window.location.origin)
            return Number(url.searchParams.get('page')) || null
          },
        },
        text: {
          default: null,
          renderHTML: (attributes) => {
            if (!attributes.text) {
              return {}
            }
            return {
              'data-text': attributes.text,
            }
          },
          parseHTML: (element) => element.getAttribute('data-text'),
        },
      }
    },

    renderHTML({ HTMLAttributes }) {
      return ['a', mergeAttributes(HTMLAttributes, { 'data-type': 'content-link' }), 0]
    },

    parseHTML() {
      return [{ tag: 'a[data-type="content-link"]' }]
    },

    addOptions() {
      return {
        openOnClick: true,
      }
    },

    addCommands() {
      return {
        setContentLink:
          (attributes) =>
          ({ commands }) => {
            return commands.setMark(this.name, attributes)
          },

        toggleContentLink:
          (attributes) =>
          ({ commands }) => {
            return commands.toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
          },

        unsetContentLink:
          () =>
          ({ commands }) => {
            return commands.unsetMark(this.name, { extendEmptyMarkRange: true })
          },
      }
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handleClick(view, pos, event) {
              // Single click should not work in edit mode
              if (view.editable) {
                return false
              }

              const { state } = view
              const { doc, schema } = state
              const range = doc
                .resolve(pos)
                .marks()
                .find((mark) => mark.type === schema.marks.contentLink)

              if (range) {
                event.preventDefault()
                const { target, page } = range.attrs
                if (target) {
                  openFile({ fileId: target, pageIndex: page ?? null })
                  return true
                }
              }
              return false
            },
          },
        }),
      ]
    },
  })

export default createContentLink

export function handleContentLinkClick(
  event: MouseEvent,
  openFile: (payload: { fileId: string; pageIndex?: number }) => void,
) {
  const target = event.target as HTMLElement
  const contentLinkElement = target.matches('a[data-type="content-link"]')
    ? target
    : target.closest('a[data-type="content-link"]')
  if (contentLinkElement) {
    event.preventDefault()
    const href = contentLinkElement.getAttribute('href')
    if (href) {
      const url = new URL(href, window.location.origin)
      const fileId = url.searchParams.get('file')
      const page = url.searchParams.get('page')
      if (fileId) {
        openFile({ fileId, pageIndex: Number(page) || undefined })
      }
    }
    return true
  }
}
